import {Container} from "@mui/material";
import {Link} from "react-router-dom";
import deadlocket from "../../data/deadlocket";
import "./DeadlocketProject.css";

const Screenshot = ({src, alt, caption, width, height, className = ""}) => (
    <figure className={`deadlocket-screenshot ${className}`}>
        <a href={src} target="_blank" rel="noreferrer" aria-label={`Open full-size screenshot: ${alt}`}>
            <img src={src} alt={alt} width={width} height={height} loading="lazy" decoding="async"/>
        </a>
        {caption && <figcaption>{caption}</figcaption>}
    </figure>
);

const DeadlocketProject = () => (
    <Container>
        <main className="deadlocket-project">
            <Link className="deadlocket-back" to="/projects">← All projects</Link>

            <header className="deadlocket-intro">
                <div className="deadlocket-title-row">
                    <h1>{deadlocket.title}</h1>
                    <a className="deadlocket-visit" href={deadlocket.website} target="_blank" rel="noreferrer">
                        Visit deadlocket.com <span aria-hidden="true">↗</span>
                    </a>
                </div>
                <p className="deadlocket-lead">{deadlocket.description}</p>
                <dl className="deadlocket-facts">
                    <div><dt>My role</dt><dd>Design & development</dd></div>
                    <div><dt>Platform</dt><dd>Windows desktop & web</dd></div>
                    <div><dt>Project</dt><dd>Independent · 2026</dd></div>
                </dl>
            </header>

            <Screenshot
                className="deadlocket-main-shot"
                src="/images/deadlocket/hero-tiers.png"
                width={1384} height={927}
                alt="DEADLOCKET hero tier list with hero portraits, win rates and filters for game mode, match type, time window and rank"
                caption="Hero research starts with the right context: game mode, match type, time window and rank."
            />

            <section className="deadlocket-overview" aria-labelledby="deadlocket-overview-title">
                <h2 id="deadlocket-overview-title">Every stage.<br/>One companion.</h2>
                <p>I built DEADLOCKET to bring hero research, community builds and match analysis into one desktop app. Its Windows integration adds live information to the game, while the companion website handles downloads and release information.</p>
            </section>

            <section className="deadlocket-feature deadlocket-builds" aria-labelledby="deadlocket-builds-title">
                <div className="deadlocket-feature-copy">
                    <h2 id="deadlocket-builds-title">Know your hero.<br/>Find your build.</h2>
                    <p>Explore abilities, items, performance curves, counters and synergies. Filter hero statistics by mode, time window and rank to put each result in context.</p>
                    <p>Browse community builds, compare item paths and ability orders, and add a build to Deadlock’s My Builds. Imports apply after the game closes, with DEADLOCKET running.</p>
                </div>
                <Screenshot
                    src="/images/deadlocket/build-browser.png"
                    width={1050} height={814}
                    alt="DEADLOCKET desktop build browser showing community build choices and Wraith item paths"
                    caption="Community builds, complete item paths and ability orders."
                />
            </section>

            <section className="deadlocket-feature deadlocket-live" aria-labelledby="deadlocket-live-title">
                <div className="deadlocket-overlay-images" data-ui="true">
                    <Screenshot
                        src="/images/deadlocket/performance-overlay.png"
                        width={272} height={189}
                        alt="Standard performance overlay comparing net worth, hero damage and objective damage with a rank target"
                        caption="Live performance"
                    />
                    <Screenshot
                        src="/images/deadlocket/objective-timers.png"
                        width={268} height={233}
                        alt="In-game objective timer overlay with Bridge buffs, Boxes, Plaza, Tower, Sinners and Mid Boss"
                        caption="Objective timers"
                    />
                    <Screenshot
                        className="deadlocket-brawl-shot"
                        src="/images/deadlocket/brawl-item.png"
                        width={443} height={469}
                        alt="Street Brawl Spirit Resilience item with an S grade, win rate and pick rate"
                        caption="Street Brawl item grades"
                    />
                </div>
                <div className="deadlocket-feature-copy">
                    <h2 id="deadlocket-live-title">More context.<br/>While you play.</h2>
                    <p>Compare live performance with hero benchmarks, keep track of objectives and see Street Brawl item grades, win rates and pick rates alongside your draft choices.</p>
                    <p>Passive overlays stay click-through. Position and opacity controls let players fit the information around their HUD.</p>
                </div>
            </section>

            <section className="deadlocket-review" aria-labelledby="deadlocket-review-title">
                <div className="deadlocket-overview">
                    <h2 id="deadlocket-review-title">The whole match.<br/>In perspective.</h2>
                    <p>Search player profiles, follow rank progression and revisit match history. Detailed reports bring both teams’ K/D/A, damage, healing and final inventories together for a closer look at each game.</p>
                </div>
                <Screenshot
                    src="/images/deadlocket/match-report.png"
                    width={1210} height={388}
                    alt="DEADLOCKET match report comparing both teams’ heroes, ranks, K/D/A, damage, healing and final items"
                    caption="Match reports show the team comparison alongside each player’s final inventory."
                />
            </section>

            <section className="deadlocket-engineering" aria-labelledby="deadlocket-engineering-title">
                <h2 id="deadlocket-engineering-title">Under the hood.</h2>
                <p className="deadlocket-engineering-intro">One project spanning a desktop interface, native Windows integration and a public website.</p>
                <dl className="deadlocket-stack">
                    <div>
                        <dt>Desktop application</dt>
                        <dd className="deadlocket-technologies">Electron · React · TypeScript · Vite</dd>
                        <dd>Hero analytics, player profiles and build browsing, backed by validated public API data and local caches.</dd>
                    </div>
                    <div>
                        <dt>Native Windows integration</dt>
                        <dd className="deadlocket-technologies">C# · .NET · Win32</dd>
                        <dd>A read-only game-memory integration with game-version checks and separate native build-cache import.</dd>
                    </div>
                    <div>
                        <dt>Website & services</dt>
                        <dd className="deadlocket-technologies">TypeScript · Vite · Cloudflare Workers · D1</dd>
                        <dd>The public product site, download counters and private feedback inbox, with owner access through Cloudflare Access.</dd>
                    </div>
                    <div>
                        <dt>Distribution & validation</dt>
                        <dd className="deadlocket-technologies">Electron Forge · NSIS · Vitest · Zod</dd>
                        <dd>A Windows installer and automatic update downloads through GitHub Releases, with Ed25519 manifest verification and an explicit restart to install.</dd>
                    </div>
                </dl>
            </section>

            <footer className="deadlocket-close">
                <div>
                    <h2>Explore DEADLOCKET.</h2>
                    <p>See the app, its features and the latest Windows release.</p>
                </div>
                <a className="deadlocket-visit" href={deadlocket.website} target="_blank" rel="noreferrer">
                    Visit deadlocket.com <span aria-hidden="true">↗</span>
                </a>
            </footer>
            <p className="deadlocket-credit">Screenshots from DEADLOCKET. Deadlock and its game artwork belong to <a href="https://www.playdeadlock.com/" target="_blank" rel="noreferrer">Valve</a>.</p>
        </main>
    </Container>
);

export default DeadlocketProject;
